/* Mantleford analytics preferences. Shopify owns consent persistence; no Google calls. */
(() => {
  'use strict';
  const dialog = document.querySelector('[data-mf-analytics-dialog]');
  if (!dialog || dialog.dataset.mfPreferencesReady === 'true') return;
  dialog.dataset.mfPreferencesReady = 'true';
  const form = dialog.querySelector('form');
  const choice = dialog.querySelector('[data-mf-analytics-choice]');
  const save = dialog.querySelector('[data-mf-analytics-save]');
  const cancel = dialog.querySelector('[data-mf-analytics-cancel]');
  const errorNode = dialog.querySelector('[data-mf-analytics-error]');
  const loading = dialog.querySelector('[data-mf-analytics-loading]');
  const status = document.querySelector('[data-mf-analytics-status]');
  if (![form, choice, save, cancel, errorNode, loading, status].every(Boolean)) return;
  let opener = null, ready = false, pending = false, openVersion = 0, saveVersion = 0;
  let loadingPromise = null;
  const api = () => {
    const candidate = window.Shopify && window.Shopify.customerPrivacy;
    return candidate && typeof candidate.analyticsProcessingAllowed === 'function' &&
      typeof candidate.setTrackingConsent === 'function' ? candidate : null;
  };
  const showError = message => {
    errorNode.textContent = message;
    errorNode.hidden = !message;
  };
  const controls = () => {
    choice.disabled = !ready || pending;
    save.disabled = !ready || pending;
    cancel.disabled = pending;
    form.setAttribute('aria-busy', pending ? 'true' : 'false');
    save.textContent = pending ? 'Saving…' : 'Save preferences';
  };
  function loadApi() {
    if (api()) return Promise.resolve(api());
    if (loadingPromise) return loadingPromise;
    loadingPromise = new Promise((resolve, reject) => {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        reject(new Error('Privacy settings are unavailable.')); return;
      }
      let finished = false;
      const timer = setTimeout(() => finish(new Error('Privacy settings did not load.')), 8000);
      function finish(error) {
        if (finished) return;
        finished = true; clearTimeout(timer);
        const loaded = api();
        if (error || !loaded) reject(new Error('Privacy settings are unavailable.'));
        else resolve(loaded);
      }
      try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish); }
      catch (error) { finish(error); }
    }).finally(() => { loadingPromise = null; });
    return loadingPromise;
  }
  async function open(trigger) {
    if (dialog.open) return;
    opener = trigger;
    status.textContent = '';
    if (typeof dialog.showModal !== 'function') {
      status.textContent = 'Analytics preferences could not open in this browser. Please try an updated browser.';
      return;
    }
    const version = ++openVersion;
    ready = false; pending = false; showError(''); controls();
    loading.hidden = false;
    dialog.showModal();
    cancel.focus();
    try {
      const privacy = await loadApi();
      if (version !== openVersion || !dialog.open) return;
      choice.checked = privacy.analyticsProcessingAllowed() === true;
      ready = true;
    } catch (_) {
      if (version === openVersion && dialog.open) {
        showError('We couldn’t load your analytics preferences. Close this window and try again. Your choice has not been changed.');
      }
    } finally {
      if (version === openVersion && dialog.open) { loading.hidden = true; controls(); }
    }
  }
  document.addEventListener('click', event => {
    const trigger = event.target instanceof Element && event.target.closest('[data-mf-analytics-open]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });
  cancel.addEventListener('click', () => { if (!pending) dialog.close(); });
  dialog.addEventListener('cancel', event => {
    if (pending) event.preventDefault();
  });
  dialog.addEventListener('close', () => {
    ++openVersion;
    if (opener && opener.isConnected) opener.focus();
  });
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (!ready || pending || !dialog.open) return;
    const privacy = api();
    if (!privacy) {
      ready = false; controls();
      showError('Privacy settings are unavailable. Close this window and try again. Your choice has not been changed.');
      return;
    }
    const requested = choice.checked;
    const version = ++saveVersion;
    const openedAtSave = openVersion;
    pending = true; showError(''); controls();
    const timer = setTimeout(() => {
      if (version !== saveVersion || !pending) return;
      pending = false; controls();
      showError('We couldn’t confirm that your preference was saved. Close and reopen this window to check it, or try again.');
    }, 8000);
    function completed(error) {
      if (version !== saveVersion) return;
      clearTimeout(timer);
      let allowed;
      try {
        if (error) throw new Error('Save failed.');
        allowed = privacy.analyticsProcessingAllowed() === true;
        const stored = typeof privacy.currentVisitorConsent === 'function' ? privacy.currentVisitorConsent().analytics : null;
        if (stored !== null ? stored !== (requested ? 'yes' : 'no') : allowed !== requested) throw new Error('Save not confirmed.');
      } catch (_) {
        if (openedAtSave !== openVersion || !dialog.open) return;
        pending = false; controls();
        showError('We couldn’t confirm that your preference was saved. Close and reopen this window to check it, or try again.');
        return;
      }
      // Shopify also emits visitorConsentCollected when consent changes. This explicit
      // verified callback lets our adapter recheck even when the saved value is unchanged.
      document.dispatchEvent(new CustomEvent('mantleford:analytics-preferences-saved', { detail: { analyticsAllowed: allowed } }));
      // A timed-out request can finish after the visitor has closed or reopened the
      // dialog. Recheck analytics, but do not close their new dialog or steal focus.
      if (openedAtSave !== openVersion || !dialog.open) return;
      pending = false; controls();
      status.textContent = 'Your analytics preference has been saved.';
      dialog.close();
    }
    try { privacy.setTrackingConsent({ analytics: requested }, completed); }
    catch (error) { completed(error); }
  });
  controls();
})();
